import { useCallback, useMemo, useState } from "react";

import { Badge } from "@/design-system/components/Badge/Badge";
import { Button } from "@/design-system/components/Button/Button";
import { Checkbox } from "@/design-system/components/Checkbox/Checkbox";
import {
  Dialog,
  DialogActionsWrapper,
} from "@/design-system/components/Dialog/Dialog";
import { Input } from "@/design-system/components/Input/Input";
import { Select } from "@/design-system/components/Select/Select";
import { ColumnConfig, Table } from "@/design-system/components/Table/Table";
import { toast } from "@/design-system/components/Toast/Toast";

import { CopyToClipboardButton } from "@/CopyToClipboardButton";

import {
  useCreateDocsAccessCode,
  useDocsAccessCodes,
  useRevokeDocsAccessCode,
} from "./queries";
import type { DocsAccessCode, ExpiryTimeframe } from "./types";
import { useSetBreadcrumbs } from "@/NavBar/NavigationLayoutContext";

const EXPIRY_OPTIONS: { value: ExpiryTimeframe; label: string }[] = [
  { value: "1_hour", label: "1 hour" },
  { value: "1_day", label: "1 day" },
  { value: "1_week", label: "1 week" },
  { value: "1_month", label: "1 month" },
  { value: "3_month", label: "3 months" },
];

type CodeStatus = "Active" | "Expired" | "Revoked";

function codeStatus(code: DocsAccessCode): CodeStatus {
  if (code.revoked_at) {
    return "Revoked";
  }
  if (code.expires_at && new Date(code.expires_at) < new Date()) {
    return "Expired";
  }
  return "Active";
}

function formatDate(value: string | null): string {
  if (!value) {
    return "Never";
  }
  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// A shareable link that drops a visitor straight into the docs: DocsLoginPage
// reads ?code=, auto-submits it, and redirects to the signed docs URL.
function autoLoginLink(code: string): string {
  return `${window.location.origin}/docs-login?code=${encodeURIComponent(code)}`;
}

function DocsTempAccessPage() {
  const { data: codes, isLoading } = useDocsAccessCodes();
  const createMutation = useCreateDocsAccessCode();
  const revokeMutation = useRevokeDocsAccessCode();

  const [description, setDescription] = useState("");
  const [expiry, setExpiry] = useState<ExpiryTimeframe>("1_week");
  const [hideInactive, setHideInactive] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<DocsAccessCode | null>(null);

  const visibleCodes = useMemo(() => {
    const all = codes ?? [];
    if (!hideInactive) {
      return all;
    }
    return all.filter((c) => codeStatus(c) === "Active");
  }, [codes, hideInactive]);

  const handleCreate = async () => {
    try {
      const created = await createMutation.mutateAsync({ description, expiry });
      toast.success(`Created access code ${created.code}`);
      setDescription("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Something went wrong");
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) {
      return;
    }
    try {
      await revokeMutation.mutateAsync(revokeTarget.id);
      toast.success("Access code revoked");
      setRevokeTarget(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Something went wrong");
    }
  };

  const columnConfigs = useMemo<ColumnConfig<DocsAccessCode>[]>(
    () => [
      {
        label: "Description",
        align: "left",
        CellContent: ({ row }) => (
          <div className="text-textDefault line-clamp-2 max-w-md">
            {row.description || "—"}
          </div>
        ),
      },
      {
        label: "Code",
        align: "left",
        CellContent: ({ row }) => (
          <div className="flex items-center gap-1">
            <span className="text-textDefault font-mono">{row.code}</span>
            <CopyToClipboardButton text={row.code} title="Copy code" />
          </div>
        ),
      },
      {
        label: "Status",
        align: "left",
        CellContent: ({ row }) => {
          const status = codeStatus(row);
          return (
            <Badge
              text={status}
              variant={status === "Active" ? "success" : "unknown"}
              className="min-w-0"
            />
          );
        },
      },
      {
        label: "Expires",
        align: "left",
        getCellClassName: () => "text-textSubtle",
        getCellText: (row) => formatDate(row.expires_at),
      },
      {
        label: "Created",
        align: "left",
        getCellClassName: () => "text-textSubtle",
        getCellText: (row) => formatDate(row.created_at),
      },
      {
        label: "Created by",
        align: "left",
        getCellClassName: () => "text-textSubtle",
        getCellText: (row) => row.created_by_email || "—",
      },
      {
        label: "Actions",
        align: "right",
        width: "16rem",
        CellContent: ({ row }) => {
          const isActive = codeStatus(row) === "Active";
          return (
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  void navigator.clipboard.writeText(autoLoginLink(row.code));
                  toast.success("Auto-login link copied");
                }}
              >
                Copy login link
              </Button>
              {isActive && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setRevokeTarget(row)}
                >
                  Revoke
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [],
  );

  const EmptyState = useCallback(
    () => (
      <div className="text-textSubtle p-8 text-center text-sm">
        {hideInactive && (codes?.length ?? 0) > 0
          ? "No active codes. Turn off the filter to see inactive ones."
          : "No access codes yet. Create one above."}
      </div>
    ),
    [hideInactive, codes],
  );

  const breadcrumbs = [
    {
      label: "Internal Tools",
      href: "/admin",
      disabled: true,
    },
    {
      label: "Docs Temp Access",
      href: "/admin/docs-temp-access",
    },
  ];

  useSetBreadcrumbs(breadcrumbs);

  return (
    <>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <h1 className="text-lg font-semibold">Docs Temp Access</h1>
          <p className="text-textSubtle text-sm">
            Generate temporary access codes that let customers into the
            published docs without a Freshpaint account. Codes are reusable
            until they expire or are revoked.
          </p>
        </div>

        <div className="border-borderSubtle flex flex-wrap items-end gap-4 rounded-lg border bg-white p-4 shadow dark:bg-gray-100">
          <div className="min-w-64 flex-1">
            <Input
              labelPosition="stacked"
              id="docs-code-description"
              label="Description"
              placeholder="E.g. Acme Corp evaluation"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label
              htmlFor="docs-code-expiry"
              className="text-textDefault text-sm"
            >
              Expires
            </label>
            <Select
              options={EXPIRY_OPTIONS}
              getOptionLabel={(o) => o.label}
              getOptionValue={(o) => o.value}
              rootProps={{
                value: expiry,
                onValueChange: (v) => setExpiry(v as ExpiryTimeframe),
              }}
              triggerProps={{ id: "docs-code-expiry", className: "w-48" }}
            />
          </div>
          <Button loading={createMutation.isLoading} onClick={handleCreate}>
            Create code
          </Button>
        </div>

        <div className="flex items-center justify-end">
          <label className="text-textSubtle flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox
              checked={hideInactive}
              onCheckedChange={(checked) => setHideInactive(checked === true)}
            />
            Hide inactive codes
          </label>
        </div>

        <Table
          rows={visibleCodes}
          getRowId={(row) => row.id}
          columnConfigs={columnConfigs}
          EmptyState={EmptyState}
          loading={isLoading}
        />
      </div>

      <Dialog
        open={!!revokeTarget}
        onOpenChange={() => setRevokeTarget(null)}
        title="Revoke access code?"
        description={
          revokeTarget
            ? `Revoking "${revokeTarget.code}" immediately blocks it from signing anyone into the docs. This cannot be undone.`
            : ""
        }
      >
        <DialogActionsWrapper>
          <Button
            type="button"
            variant="link"
            className="text-textSubtle"
            onClick={() => setRevokeTarget(null)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            loading={revokeMutation.isLoading}
            onClick={handleRevoke}
          >
            Revoke
          </Button>
        </DialogActionsWrapper>
      </Dialog>
    </>
  );
}

export default DocsTempAccessPage;
